import { Viewer, Worker } from "@react-pdf-viewer/core";
import DOMPurify from "dompurify";
import {
  FiEye,
  FiFile,
  FiFileText,
  FiImage,
  FiX,
  FiPrinter,
  FiVideo,
  FiDownload,
} from "react-icons/fi";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
const COLORS = {
  blue: "#063b87",
  blueLight: "#0a57bb",
  green: "#087344",
  background: "#f3f6fb",
  text: "#243247",
};
const getFileType = (doc) => {
  const mime = String(doc?.mime_type || "").toLowerCase();
  const name = String(doc?.original_file_name || "").toLowerCase();

  if (
    mime.startsWith("image/") ||
    /\.(png|jpe?g|gif|webp|bmp|svg|avif|ico|tiff?)$/i.test(name)
  ) {
    return "image";
  }

  if (mime === "application/pdf" || /\.pdf$/i.test(name)) {
    return "pdf";
  }

  if (
    mime.startsWith("video/") ||
    /\.(mp4|webm|ogg|ogv|mov|m4v|avi|mkv|mpeg|mpg|3gp)$/i.test(name)
  ) {
    return "video";
  }

  return "file";
};

const getDocumentUrl = (doc) => {
  const rawUrl =
    doc?.url ||
    doc?.download_url ||
    doc?.file_url ||
    doc?.document_url ||
    doc?.path ||
    "";

  if (typeof rawUrl !== "string" || !rawUrl.trim()) return "";

  const url = rawUrl.trim();

  // Reject executable or HTML data URLs.
  if (/^(javascript|vbscript|data):/i.test(url)) return "";

  // Accept HTTP(S), local blob URLs, and relative API paths.
  if (/^(https?:\/\/|blob:)/i.test(url)) {
    try {
      const parsed = new URL(url);

      if (
        parsed.protocol === "http:" ||
        parsed.protocol === "https:" ||
        parsed.protocol === "blob:"
      ) {
        return url;
      }

      return "";
    } catch {
      return "";
    }
  }

  if (url.startsWith("//")) return "";

  return url;
};

const escapeHtml = (value) =>
  String(value ?? "").replace(/[&<>"']/g, (char) => {
    const entities = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    };

    return entities[char];
  });

export function MessageDetailsModal({ message, loading, error, onClose }) {
  const formatBytes = (bytes) => {
    if (bytes == null || !Number.isFinite(Number(bytes))) return "";

    const size = Number(bytes);

    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;

    return `${(size / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  if (!message && !loading && !error) return null;

  const documents = Array.isArray(message?.documents) ? message.documents : [];

  const safeHtml = DOMPurify.sanitize(message?.message || "", {
    USE_PROFILES: { html: true },
  });

  const handlePrint = () => {
    if (!message) return;

    const printWindow = window.open("", "_blank", "width=1000,height=750");

    if (!printWindow) {
      alert("Please allow pop-ups to print the message.");
      return;
    }

    const safeMessageHtml = DOMPurify.sanitize(message.message || "", {
      USE_PROFILES: { html: true },
    });

    const attachmentHtml = documents.length
      ? documents
          .map(
            (doc) => `
              <li>
                <strong>${escapeHtml(
                  doc.original_file_name || "Attachment",
                )}</strong>
                <span> (${escapeHtml(
                  doc.mime_type || "Unknown file type",
                )})</span>
              </li>
            `,
          )
          .join("")
      : "<li>No attachments</li>";

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <title>${escapeHtml(message.subject || "Message Details")}</title>
          <style>
            @page {
              size: A4;
              margin: 15mm;
            }

            body {
              position: relative;
              font-family: Arial, sans-serif;
              color: #243247;
              font-size: 12px;
              line-height: 1.6;
              overflow-wrap: anywhere;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .watermark {
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%) rotate(-35deg);
              color: rgba(6, 59, 135, 0.09);
              font-size: 75px;
              font-weight: 900;
              letter-spacing: 8px;
              white-space: nowrap;
              pointer-events: none;
              z-index: -1;
            }

            .message {
              position: relative;
              z-index: 1;
            }

            img, video, iframe {
              max-width: 100%;
            }

            table {
              max-width: 100%;
              border:1px solid black;
              border-collapse: collapse;
            }

            td, th {
            border:1px solid black;
              overflow-wrap: anywhere;
            }

            .footer {
              margin-top: 30px;
              padding-top: 10px;
              border-top: 1px solid #ddd;
              color: #64748b;
              font-size: 10px;
            }

            @media print {
              .watermark {
                position: fixed;
              }
            }
          </style>
        </head>
        <body>
          <div class="watermark">RAILSETU</div>

          <h2>Message Content</h2>
          <div class="message">
            ${safeMessageHtml || "No message content available."}
          </div>

          <h2>Attachments (${documents.length})</h2>
          <ul>${attachmentHtml}</ul>

          <div class="footer">
            Generated from RailSetu Message Management
          </div>

          <script>
            window.onload = function () {
              window.focus();
              window.print();
            };

            window.onafterprint = function () {
              window.close();
            };
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-2 p-md-4"
      style={{
        background: "rgba(9, 25, 48, .65)",
        zIndex: 1055,
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        className="bg-white shadow-lg w-100 d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-label="Message details"
        style={{
          maxWidth: 1000,
          maxHeight: "94vh",
          borderRadius: 16,
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          className="p-3 p-md-4 text-white d-flex justify-content-between align-items-start gap-3 flex-shrink-0"
          style={{
            background: `linear-gradient(120deg, ${COLORS.blue}, ${COLORS.blueLight}, ${COLORS.green})`,
          }}
        >
          <div className="min-w-0">
            <h5 className="fw-bold mb-1 text-break">
              {message?.subject || "Message details"}
            </h5>

            {message && (
              <div className="small opacity-75">Message ID: {message.id}</div>
            )}
          </div>

          <button
            type="button"
            className="btn btn-sm btn-light  flex-shrink-0"
            aria-label="Close message details"
            onClick={onClose}
          >
            <FiX size={19} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="p-3 p-md-4 overflow-auto flex-grow-1">
          {loading && (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
              <div className="small text-secondary mt-3">
                Loading message details...
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {!loading && message && (
            <>
              {/* Message metadata */}
              <div className="row g-3 mb-4">
                {[
                  ["Type", message.type],
                  ["Sender", message.msg_sender],
                  ["Message date", formatDate(message.date)],
                  ["Reminder date", formatDate(message.reminder_date)],
                ].map(([label, value]) => (
                  <div className="col-12 col-sm-6 col-lg-3" key={label}>
                    <div
                      className="p-3 h-100 rounded-3"
                      style={{ background: COLORS.background }}
                    >
                      <div className="small text-secondary mb-1">{label}</div>
                      <div className="fw-semibold text-break">
                        {value || "—"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message content */}
              <div className="mb-4">
                <h6 className="fw-bold mb-3">Message content</h6>

                <div
                  className="border rounded-3 p-3 p-md-4"
                  style={{
                    color: COLORS.text,
                    overflowWrap: "anywhere",
                    overflowX: "auto",
                    lineHeight: 1.7,
                  }}
                >
                  {message.message ? (
                    <div
                      className="text-start"
                      // className="message-html-content"
                      dangerouslySetInnerHTML={{ __html: safeHtml }}
                    />
                  ) : (
                    <span className="text-secondary">
                      No message content available.
                    </span>
                  )}
                </div>
              </div>

              {/* Attachments */}
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-bold mb-0">Attachments</h6>

                  <span className="badge text-bg-light border">
                    {documents.length} file(s)
                  </span>
                </div>

                {documents.length === 0 ? (
                  <div className="border rounded-3 p-4 text-center text-secondary">
                    <FiFile size={28} className="mb-2" />
                    <div>No attachments for this message.</div>
                  </div>
                ) : (
                  <div className="row g-3">
                    {documents.map((doc, index) => {
                      const fileType = getFileType(doc);
                      const documentUrl = getDocumentUrl(doc);
                      const fileName = doc.original_file_name || "Attachment";

                      const icon =
                        fileType === "image" ? (
                          <FiImage size={23} />
                        ) : fileType === "video" ? (
                          <FiVideo size={23} />
                        ) : fileType === "pdf" ? (
                          <FiFileText size={23} />
                        ) : (
                          <FiFile size={23} />
                        );

                      const iconColor =
                        fileType === "image"
                          ? "#087344"
                          : fileType === "video"
                            ? "#6f42c1"
                            : fileType === "pdf"
                              ? "#dc3545"
                              : COLORS.blue;

                      const iconBackground =
                        fileType === "image"
                          ? "#e9f7ef"
                          : fileType === "video"
                            ? "#f1eaff"
                            : fileType === "pdf"
                              ? "#fff0f0"
                              : "#edf3ff";

                      return (
                        <div
                          className="col-12"
                          key={
                            doc.document_id ?? doc.id ?? `${fileName}-${index}`
                          }
                        >
                          <div className="border rounded-3 p-3">
                            {/* File details */}
                            <div className="d-flex gap-3 align-items-center">
                              <div
                                className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                  width: 46,
                                  height: 46,
                                  background: iconBackground,
                                  color: iconColor,
                                }}
                              >
                                {icon}
                              </div>

                              <div className="flex-grow-1 min-w-0">
                                <div
                                  className="fw-semibold small text-break"
                                  title={fileName}
                                >
                                  {fileName}
                                </div>

                                <div className="small text-secondary mt-1">
                                  {doc.mime_type || fileType.toUpperCase()}
                                  {doc.file_size != null &&
                                    ` · ${formatBytes(doc.file_size)}`}
                                </div>
                              </div>

                              {/* {documentUrl && (
                                <a
                                  href={documentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary flex-shrink-0"
                                  title="Open attachment in a new tab"
                                >
                                  <FiEye />
                                </a>
                              )} */}
                            </div>

                            {/* Inline image preview */}
                            {documentUrl && fileType === "image" && (
                              <div
                                className="mt-3 rounded-3 text-center p-2"
                                style={{ background: COLORS.background }}
                              >
                                <a
                                  href={documentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={`Open ${fileName}`}
                                >
                                  <img
                                    src={documentUrl}
                                    alt={fileName}
                                    loading="lazy"
                                    className="img-fluid rounded-2"
                                    style={{
                                      maxHeight: 380,
                                      objectFit: "contain",
                                    }}
                                  />
                                </a>
                              </div>
                            )}

                            {/* Inline video player */}
                            {documentUrl && fileType === "video" && (
                              <div
                                className="mt-3 rounded-3 overflow-hidden"
                                style={{ background: "#111" }}
                              >
                                <video
                                  controls
                                  playsInline
                                  preload="metadata"
                                  className="w-100 d-block"
                                  style={{
                                    maxHeight: 440,
                                    objectFit: "contain",
                                  }}
                                >
                                  <source
                                    src={documentUrl}
                                    type={
                                      doc.mime_type?.startsWith("video/")
                                        ? doc.mime_type
                                        : undefined
                                    }
                                  />
                                  Your browser does not support HTML video.
                                  <a href={documentUrl}>Open video</a>
                                </video>
                              </div>
                            )}

                            {/* Inline PDF viewer */}
                            {documentUrl && fileType === "pdf" && (
                              <div
                                className="mt-3 border rounded-3 overflow-hidden"
                                style={{
                                  background: COLORS.background,
                                  width: "100%",
                                }}
                              >
                                <div
                                  style={{
                                    height: "min(65vh, 650px)",
                                    minHeight: "350px",
                                    width: "100%",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                                    <Viewer
                                      fileUrl={documentUrl}
                                      plugins={[defaultLayoutPlugin()]}
                                      defaultScale={1}
                                      renderError={(error) => (
                                        <div className="alert alert-danger m-3">
                                          <strong>
                                            Unable to display PDF.
                                          </strong>
                                          <div className="small mt-2">
                                            {error?.message ||
                                              "The PDF could not be loaded."}
                                          </div>
                                          <a
                                            href={documentUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-sm btn-outline-danger mt-2"
                                          >
                                            Open PDF directly
                                          </a>
                                        </div>
                                      )}
                                      renderLoader={() => (
                                        <div className="d-flex flex-column align-items-center justify-content-center h-100 p-4">
                                          <div
                                            className="spinner-border text-primary"
                                            role="status"
                                          />
                                          <span className="small text-secondary mt-2">
                                            Loading PDF...
                                          </span>
                                        </div>
                                      )}
                                    />
                                  </Worker>
                                </div>
                              </div>
                            )}

                            {/* Unsupported or missing URL */}
                            {!documentUrl && (
                              <div className="small text-secondary mt-3">
                                The attachment URL is missing. Check the
                                document data returned by your API.
                              </div>
                            )}

                            {/* Download */}
                            {documentUrl && (
                              <div className="mt-3 d-flex justify-content-end">
                                <a
                                  href={documentUrl}
                                  download={fileName}
                                  className="btn btn-sm btn-light border"
                                >
                                  <FiDownload className="me-1" />
                                  Download
                                </a>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-top d-flex justify-content-end gap-2 flex-shrink-0 bg-white">
          {message && !loading && !error && (
            <button
              type="button"
              className="btn btn-sm btn-primary d-flex align-items-center gap-2"
              style={{
                background: COLORS.blue,
                borderColor: COLORS.blue,
              }}
              onClick={handlePrint}
              title="Print message details"
            >
              <FiPrinter size={17} />
              Print
            </button>
          )}

          <button
            type="button"
            className="btn btn-sm btn-light border"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
